import type { SessionData } from '../types';

// ============================================
// Multi-layer identity persistence
// Stores visitor token in localStorage + cookie + indexedDB
// so clearing one doesn't lose the visitor identity.
// ============================================

const VISITOR_TOKEN_KEY = 'cevro_visitor_token';
const SESSION_KEY = 'cevro_session';
const COOKIE_MAX_AGE_SECONDS = 270 * 24 * 60 * 60; // 9 months (same as Intercom)
const IDB_DB_NAME = 'cevro_sdk';
const IDB_STORE_NAME = 'identity';

// --- Cookie helpers ---

function setCookie(name: string, value: string): void {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)};max-age=${COOKIE_MAX_AGE_SECONDS};path=/;SameSite=Lax`;
  } catch {
    // Cookies may be blocked
  }
}

function getCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function deleteCookie(name: string): void {
  try {
    document.cookie = `${name}=;max-age=0;path=/;SameSite=Lax`;
  } catch {
    // Ignore
  }
}

// --- IndexedDB helpers (async, fire-and-forget for writes) ---

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbSet(key: string, value: string): void {
  // Fire-and-forget — don't block on write
  openIDB()
    .then(db => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      tx.objectStore(IDB_STORE_NAME).put(value, key);
      tx.oncomplete = () => db.close();
      tx.onerror = () => db.close();
    })
    .catch(() => {});
}

function idbGet(key: string): Promise<string | null> {
  return openIDB()
    .then(db => {
      return new Promise<string | null>((resolve) => {
        const tx = db.transaction(IDB_STORE_NAME, 'readonly');
        const req = tx.objectStore(IDB_STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => resolve(null);
        tx.oncomplete = () => db.close();
        tx.onerror = () => db.close();
      });
    })
    .catch(() => null);
}

function idbDelete(key: string): void {
  openIDB()
    .then(db => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      tx.objectStore(IDB_STORE_NAME).delete(key);
      tx.oncomplete = () => db.close();
      tx.onerror = () => db.close();
    })
    .catch(() => {});
}

/**
 * Collect browser session data
 */
export function collectSessionData(): SessionData {
  const session: SessionData = {};

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return session;
  }

  try {
    // Browser info
    session.userAgent = navigator.userAgent;
    session.language = navigator.language;
    session.platform = navigator.platform;
    session.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Page context
    session.currentPage = window.location.href;
    session.pageTitle = document.title;
    session.referrer = document.referrer;

    // Screen dimensions
    session.screenWidth = window.screen.width;
    session.screenHeight = window.screen.height;
    session.viewportWidth = window.innerWidth;
    session.viewportHeight = window.innerHeight;
    session.devicePixelRatio = window.devicePixelRatio;

    // UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    session.utmSource = urlParams.get('utm_source') || undefined;
    session.utmMedium = urlParams.get('utm_medium') || undefined;
    session.utmCampaign = urlParams.get('utm_campaign') || undefined;
    session.utmContent = urlParams.get('utm_content') || undefined;
    session.utmTerm = urlParams.get('utm_term') || undefined;

    // Capabilities
    session.cookiesEnabled = navigator.cookieEnabled;
    session.touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    session.online = navigator.onLine;

    // Connection info (if available)
    const connection = (navigator as any).connection;
    if (connection) {
      session.connectionType = connection.effectiveType;
    }
  } catch (e) {
    // Silently ignore errors in session collection
  }

  return session;
}

/**
 * Get stored visitor token (signed by server)
 * Checks localStorage first, then cookie fallback.
 * IndexedDB is checked asynchronously via recoverVisitorToken().
 */
export function getStoredVisitorToken(): string | null {
  // Layer 1: localStorage (fastest, synchronous)
  try {
    const fromLS = localStorage.getItem(VISITOR_TOKEN_KEY);
    if (fromLS) return fromLS;
  } catch {
    // localStorage unavailable
  }

  // Layer 2: cookie fallback
  const fromCookie = getCookie(VISITOR_TOKEN_KEY);
  if (fromCookie) {
    // Restore to localStorage
    try { localStorage.setItem(VISITOR_TOKEN_KEY, fromCookie); } catch {}
    return fromCookie;
  }

  return null;
}

/**
 * Async recovery: check IndexedDB if synchronous stores are empty.
 * Call once at boot — if a token is recovered, it's written back to all layers.
 */
export async function recoverVisitorToken(): Promise<string | null> {
  // If synchronous stores already have it, nothing to recover
  const sync = getStoredVisitorToken();
  if (sync) return sync;

  // Layer 3: IndexedDB
  try {
    const fromIDB = await idbGet(VISITOR_TOKEN_KEY);
    if (fromIDB) {
      // Restore to all synchronous layers
      try { localStorage.setItem(VISITOR_TOKEN_KEY, fromIDB); } catch {}
      setCookie(VISITOR_TOKEN_KEY, fromIDB);
      return fromIDB;
    }
  } catch {
    // IndexedDB unavailable
  }

  return null;
}

/**
 * Store visitor token received from server.
 * Writes to all three layers for maximum persistence.
 */
export function storeVisitorToken(token: string): void {
  // Layer 1: localStorage
  try { localStorage.setItem(VISITOR_TOKEN_KEY, token); } catch {}

  // Layer 2: cookie (9 months)
  setCookie(VISITOR_TOKEN_KEY, token);

  // Layer 3: indexedDB (async, fire-and-forget)
  idbSet(VISITOR_TOKEN_KEY, token);
}

/**
 * Extract visitorId from visitorToken
 * Token format: visitorId.signature
 */
export function extractVisitorIdFromToken(token: string): string | null {
  if (!token) return null;
  const lastDotIndex = token.lastIndexOf('.');
  if (lastDotIndex <= 0) return null;
  return token.substring(0, lastDotIndex);
}

/**
 * Get visitorId from stored token (or null if not available)
 */
export function getStoredVisitorId(): string | null {
  const token = getStoredVisitorToken();
  return token ? extractVisitorIdFromToken(token) : null;
}

/**
 * Get visitor ID from stored token, or null if not yet available.
 * The visitor ID is assigned by the server and stored in the visitor token.
 */
export function generateVisitorId(): string | null {
  return getStoredVisitorId();
}

/**
 * Stored session data
 */
export interface StoredSessionData {
  sessionId?: string;
  contactId?: string;
  ticketId?: string;
}

// --- Account-switch detection ---
//
// Storage keys are always plain (`cevro_session`, `cevro_visitor_token`).
// We track the last identified playerId in `cevro_active_player`.
//
// On first identify(): store the player hash, keep existing session/visitor
// data intact. The anonymous→identified merge happens server-side (backend
// links the visitorId to the playerId in initSession).
//
// On subsequent identify() with a DIFFERENT playerId: account switch detected.
// Clear session + visitor data so the new identity starts fresh, then store
// the new player hash.
//
// This optimizes for the 99% case (single account) with zero disruption,
// and only pays the reset cost on actual account switches.

const ACTIVE_PLAYER_KEY = 'cevro_active_player';

/**
 * Simple hash to avoid storing raw playerIds in localStorage.
 */
function hashForKey(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Check if identify() is being called for a different player than the
 * one currently stored. Returns true if this is an account switch that
 * requires clearing session data.
 *
 * Also stores the new player hash so subsequent calls detect correctly.
 * First-time identify (no previous player) returns false — the anonymous
 * session should be preserved for server-side merge.
 */
export function detectAndRecordPlayerSwitch(playerId: string): boolean {
  const playerHash = hashForKey(playerId);
  try {
    const stored = localStorage.getItem(ACTIVE_PLAYER_KEY);
    localStorage.setItem(ACTIVE_PLAYER_KEY, playerHash);

    // First identify — no previous player, not a switch
    if (!stored) return false;

    // Same player — not a switch
    if (stored === playerHash) return false;

    // Different player — account switch
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear the active player record (e.g. on shutdown with clearVisitorData).
 */
export function clearActivePlayer(): void {
  try {
    localStorage.removeItem(ACTIVE_PLAYER_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get stored session data
 */
export function getStoredSession(): StoredSessionData | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Store session data
 */
export function storeSession(sessionId: string, contactId: string, ticketId: string): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ sessionId, contactId, ticketId }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear stored session data
 */
export function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear stored visitor token from all layers.
 */
export function clearStoredVisitorToken(): void {
  try { localStorage.removeItem(VISITOR_TOKEN_KEY); } catch {}
  deleteCookie(VISITOR_TOKEN_KEY);
  idbDelete(VISITOR_TOKEN_KEY);
}

/**
 * Clear all stored data (session + visitor token + active player)
 */
export function clearAllStoredData(): void {
  clearStoredSession();
  clearStoredVisitorToken();
  clearActivePlayer();
}
